import type {
  AdminExportReportCsvApiArg,
  AdminExportReportCsvApiResponse,
  BatchOffBoardVehiclesApiArg,
  BatchOffBoardVehiclesApiResponse,
  DownloadFeedbackImageApiArg,
  DownloadFeedbackImageApiResponse,
  ExecuteRemoteCommandsMfaApiArg,
  ExecuteRemoteCommandsMfaApiResponse,
  ExportBatchVehiclesToCsvApiArg,
  ExportBatchVehiclesToCsvApiResponse,
  ExportOrdersV20260505ApiArg,
  ExportOrdersV20260505ApiResponse,
  ExportPendingConnectionVehiclesApiArg,
  ExportPendingConnectionVehiclesApiResponse,
  ExportPendingOnboardingVehiclesApiArg,
  ExportPendingOnboardingVehiclesApiResponse,
  ExportPeripheralsApiArg,
  ExportPeripheralsApiResponse,
  ExportReportCsvApiArg,
  ExportReportCsvApiResponse,
  ExportSelectedHomeChargeReimbursementRecordsApiArg,
  ExportSelectedHomeChargeReimbursementRecordsApiResponse,
  ExportSelectedOrAllHomeChargeReimbursementRecordsApiArg,
  ExportSelectedOrAllHomeChargeReimbursementRecordsApiResponse,
  ExportSingleHomeChargeReimbursementRecordApiArg,
  ExportSingleHomeChargeReimbursementRecordApiResponse,
  GetAlertsForCurrentUserApiArg,
  GetAlertsForCurrentUserApiResponse,
  GetDiagnosticsSummaryMetricsV20250826ApiArg,
  GetDiagnosticsSummaryMetricsV20250826ApiResponse,
  GetTripSummariesApiArg,
  GetTripSummariesApiResponse,
  GetVehicleImageUrlWithParamsApiArg,
  PostAudit20250603ExportSyncApiArg,
  PostAudit20250603ExportSyncApiResponse,
  ProcessTripsApiArg,
  ProcessTripsApiResponse,
  RetrieveSafetyEventsByTripIdsApiArg,
  RetrieveSafetyEventsByTripIdsApiResponse,
  RetryActivatePeripheralsApiArg,
  RetryActivatePeripheralsApiResponse,
  UploadFeedbackImageApiArg,
  UploadFeedbackImageApiResponse,
} from './generated-api';
import { clientApi } from './generated-api';

type VerifyFanApiArg = { orgId: string; fan: string };
type VerifyFanApiResponse = {
  result?: {
    organizationId?: string;
    fan?: string;
    verificationStatus?: 'VERIFIED';
  };
};

/**
 * Capture the return value of `injectEndpoints` so that overrides whose
 * runtime response shape differs from the generated spec (e.g.
 * `getVehicleImageUrlWithParams`, which the spec models as `unknown` but
 * which this file resolves to a `string`) can be re-exported as typed hooks.
 *
 * Consumers that need the override-typed hook should import it from `~/api`,
 * not from `~/api/generated-api` (which would give them the spec-only type).
 */
const enhancedApi = clientApi.injectEndpoints({
  endpoints: (build) => ({
    // Include fleetId in request when provided (generated endpoint only sends filters)
    getDiagnosticsSummaryMetricsV20250826: build.query<
      GetDiagnosticsSummaryMetricsV20250826ApiResponse,
      GetDiagnosticsSummaryMetricsV20250826ApiArg & { fleetId?: string }
    >({
      query: (queryArg) => ({
        url: '/assets/2025-08-26/views/diagnosticDashboardSummary',
        headers: {
          organizationId: queryArg.organizationId,
          'x-bd-organization': queryArg['x-bd-organization'],
        },
        params: {
          ...(queryArg.filters != null && { filters: queryArg.filters }),
          ...(queryArg.fleetId && { fleetId: queryArg.fleetId }),
        },
      }),
      providesTags: ['dashboard-view-controller'],
    }),

    // Override getVehicleImageUrlWithParams to capture the redirect URL
    // The API returns a 302 redirect to the actual image URL
    getVehicleImageUrlWithParams: build.query<
      string,
      GetVehicleImageUrlWithParamsApiArg
    >({
      queryFn: async (queryArg, _queryApi, _extraOptions, baseQuery) => {
        const params = new URLSearchParams();
        if (queryArg.size) params.append('size', queryArg.size);
        if (queryArg.angle) params.append('angle', queryArg.angle);
        if (queryArg.isTransparentBackground !== undefined) {
          params.append(
            'isTransparentBackground',
            String(queryArg.isTransparentBackground)
          );
        }

        const queryString = params.toString();
        const url = `/assets/2025-12-16/${queryArg.vin}/image${queryString ? `?${queryString}` : ''}`;

        const result = await baseQuery({
          url,
          responseHandler: async (response) => response.url,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as string };
      },
      providesTags: ['asset-controller'],
    }),
    getAlertsForCurrentUser: build.query<
      GetAlertsForCurrentUserApiResponse,
      GetAlertsForCurrentUserApiArg
    >({
      query: (queryArg) => ({
        url: 'alerts/2025-01-28/me',
        headers: {
          'cs-continuation-token': queryArg['cs-continuation-token'],
        },
        params: { size: queryArg.size },
      }),
      providesTags: (result) => {
        if (result?.result?.items.length) {
          return [
            ...result.result.items.map(
              ({ id }) => ({ type: 'alerts-controller', id }) as const
            ),
          ];
        }
        return [{ type: 'alerts-controller', id: 'LIST' }];
      },
    }),

    // Keep the trip history consumer on a query while routing through safety-api.
    getTripEvents: build.query<
      RetrieveSafetyEventsByTripIdsApiResponse,
      RetrieveSafetyEventsByTripIdsApiArg
    >({
      query: (queryArg) => ({
        url: 'safety/2026-03-24/events/trips',
        method: 'POST',
        body: queryArg.safetyTripEventsRequestDto,
        headers: {
          'cs-organization': queryArg['cs-organization'],
          ...(queryArg['cs-fleet'] && { 'cs-fleet': queryArg['cs-fleet'] }),
        },
      }),
      keepUnusedDataFor: 300,
      // Tag trip event results by trip ID for list invalidation.
      providesTags: (result) => {
        const tags: Array<{
          type: 'safety-center-controller-v-2';
          id: string;
        }> = [{ type: 'safety-center-controller-v-2', id: 'EVENTS_LIST' }];

        if (result?.result?.items) {
          for (const item of result.result.items) {
            tags.push({
              type: 'safety-center-controller-v-2',
              id: `events-${item.tripId}`,
            });
          }
        }

        return tags;
      },
    }),

    // Override processTrips as a query instead of mutation
    processTrips: build.query<ProcessTripsApiResponse, ProcessTripsApiArg>({
      query: (queryArg) => ({
        url: 'trips/2025-04-08/routes',
        method: 'POST',
        body: queryArg.tripRoutesRequestDto,
        headers: {
          'cs-organization': queryArg['cs-organization'],
          ...(queryArg['cs-fleet'] && { 'cs-fleet': queryArg['cs-fleet'] }),
        },
      }),
      keepUnusedDataFor: 300,
      // Tag routes by trip IDs
      providesTags: (_result, _error, arg) => {
        const tags: Array<{ type: 'trips-controller'; id: string }> = [
          { type: 'trips-controller', id: 'ROUTES_LIST' },
        ];

        // Add tags for each requested trip ID
        if (arg.tripRoutesRequestDto?.tripIds) {
          for (const tripId of arg.tripRoutesRequestDto.tripIds) {
            tags.push({ type: 'trips-controller', id: `route-${tripId}` });
          }
        }

        return tags;
      },
    }),

    // Override getTripSummaries to add cache configuration
    getTripSummaries: build.query<
      GetTripSummariesApiResponse,
      GetTripSummariesApiArg
    >({
      query: (queryArg) => ({
        url: 'trips/2025-04-08/summaries',
        headers: {
          'cs-organization': queryArg['cs-organization'],
          ...(queryArg['cs-fleet'] && { 'cs-fleet': queryArg['cs-fleet'] }),
          ...(queryArg['cs-continuation-token'] && {
            'cs-continuation-token': queryArg['cs-continuation-token'],
          }),
        },
        params: {
          vin: queryArg.vin,
          tripsStartedAfter: queryArg.tripsStartedAfter,
          tripsStartedBefore: queryArg.tripsStartedBefore,
          pageSize: queryArg.pageSize,
        },
      }),
      keepUnusedDataFor: 300,
      providesTags: ['trips-controller'],
    }),

    // batch offboarding API requires multipart/form-data in the request payload
    // RTKQuery automatically sets the content-type header to multipart/form-data if the body is FormData
    batchOffBoardVehicles: build.mutation<
      BatchOffBoardVehiclesApiResponse,
      BatchOffBoardVehiclesApiArg
    >({
      query: (queryArg) => {
        const formData = new FormData();
        formData.append('file', queryArg.body.file, 'vins.csv');
        return {
          url: '/assets/2025-03-11/boarding/vehicles/offboard',
          method: 'POST',
          body: formData,
          formData: true,
          headers: {
            'cs-organization': queryArg['cs-organization'],
            'cs-fleet': queryArg['cs-fleet'],
          },
          params: {
            remoteProvider: queryArg.remoteProvider,
            telemetryProvider: queryArg.telemetryProvider,
          },
        };
      },
      invalidatesTags: ['batch-boarding-vehicles-controller'],
    }),

    // Execute remote commands with MFA requires the credentials to be included
    // So that the remember device functionality can correctly receive and set the cookie
    executeRemoteCommandsMfa: build.mutation<
      ExecuteRemoteCommandsMfaApiResponse,
      ExecuteRemoteCommandsMfaApiArg
    >({
      query: (queryArg) => ({
        url: `/assets/mfa/2025-05-20/remotecommands/${queryArg.commandType}/${queryArg.command}/${queryArg.vin}`,
        method: 'POST',
        body: queryArg.otpPayload,
        headers: {
          'cs-organization': queryArg['cs-organization'],
          'X-Device-Fingerprint': queryArg['X-Device-Fingerprint'],
          'User-Agent': queryArg['User-Agent'],
        },
        credentials: 'include',
      }),
      invalidatesTags: ['remote-command-controller'],
    }),

    exportPendingConnectionVehicles: build.query<
      ExportPendingConnectionVehiclesApiResponse,
      ExportPendingConnectionVehiclesApiArg
    >({
      query: (queryArg) => ({
        url: '/assets/2025-10-07/views/pendingConnectionVehicles/export',
        method: 'POST',
        body: queryArg.pendingVehicleExportRequest,
        headers: {
          'cs-organization': queryArg['cs-organization'],
          ...(queryArg['cs-fleet'] && { 'cs-fleet': queryArg['cs-fleet'] }),
        },
        params: {
          ...(queryArg.size && { size: queryArg.size }),
          ...(queryArg.vin && { vin: queryArg.vin }),
          ...(queryArg.pendingConnectionStatuses && {
            pendingConnectionStatuses: queryArg.pendingConnectionStatuses,
          }),
          ...(queryArg.vehicleMakes && { vehicleMakes: queryArg.vehicleMakes }),
          ...(queryArg.fan && { fan: queryArg.fan }),
          ...(queryArg.subscriberAccountNumber && {
            subscriberAccountNumber: queryArg.subscriberAccountNumber,
          }),
          ...(queryArg.accountOwnerEmailId && {
            accountOwnerEmailId: queryArg.accountOwnerEmailId,
          }),
          ...(queryArg.stationId && { stationId: queryArg.stationId }),
          ...(queryArg.vehicleYear && { vehicleYear: queryArg.vehicleYear }),
          ...(queryArg.vehicleModel && { vehicleModel: queryArg.vehicleModel }),
        },
        responseHandler: 'text',
      }),
      keepUnusedDataFor: 30,
      providesTags: ['asset-controller'],
    }),

    exportPendingOnboardingVehicles: build.query<
      ExportPendingOnboardingVehiclesApiResponse,
      ExportPendingOnboardingVehiclesApiArg
    >({
      query: (queryArg) => ({
        url: '/assets/2025-10-07/boarding/vehicles/pending/export',
        method: 'POST',
        body: queryArg.pendingVehicleExportRequest,
        headers: {
          'cs-organization': queryArg['cs-organization'],
          ...(queryArg['cs-fleet'] && { 'cs-fleet': queryArg['cs-fleet'] }),
        },
        params: {
          ...(queryArg.size && { size: queryArg.size }),
          ...(queryArg.status && { status: queryArg.status }),
          ...(queryArg.reviewReasons && {
            reviewReasons: queryArg.reviewReasons,
          }),
          ...(queryArg.fan && { fan: queryArg.fan }),
          ...(queryArg.subscriberAccountNumber && {
            subscriberAccountNumber: queryArg.subscriberAccountNumber,
          }),
          ...(queryArg.accountOwnerEmailId && {
            accountOwnerEmailId: queryArg.accountOwnerEmailId,
          }),
          ...(queryArg.stationId && { stationId: queryArg.stationId }),
          ...(queryArg.vehicleYear && { vehicleYear: queryArg.vehicleYear }),
          ...(queryArg.vehicleMakes && { vehicleMakes: queryArg.vehicleMakes }),
          ...(queryArg.vehicleModel && { vehicleModel: queryArg.vehicleModel }),
        },
        responseHandler: 'text',
      }),
      keepUnusedDataFor: 30,
      providesTags: ['asset-controller'],
    }),

    //Overrides for reimbursement exports
    //Added responseHandler: 'text' for supporting CSV exports
    exportSingleHomeChargeReimbursementRecord: build.query<
      ExportSingleHomeChargeReimbursementRecordApiResponse,
      ExportSingleHomeChargeReimbursementRecordApiArg
    >({
      query: (queryArg) => ({
        url: `/evcharge/2025-08-26/export-single-reimbursement/${queryArg.driverId}/${queryArg.chargeSessionId}`,
        headers: {
          'cs-organization': queryArg['cs-organization'],
          'cs-fleet': queryArg['cs-fleet'],
        },
        params: {
          timeZoneId: queryArg.timeZoneId,
          distanceUnit: queryArg.distanceUnit,
          temperatureUnit: queryArg.temperatureUnit,
        },
        responseHandler: 'text',
      }),
      providesTags: ['reimbursements-export-and-reports-controller'],
    }),
    exportBatchVehiclesToCsv: build.query<
      ExportBatchVehiclesToCsvApiResponse,
      ExportBatchVehiclesToCsvApiArg
    >({
      query: (queryArg) => ({
        url: `/assets/2025-09-23/boarding/batches/${queryArg.batchId}/export`,
        responseHandler: 'text',
        method: 'GET',
        headers: {
          'cs-organization': queryArg['cs-organization'],
          ...(queryArg['cs-fleet'] && { 'cs-fleet': queryArg['cs-fleet'] }),
        },
        params: { vin: queryArg.vin, status: queryArg.status },
      }),
      providesTags: ['batch-boarding-vehicles-controller'],
    }),
    //Overrides for reimbursement exports
    //Added responseHandler: 'text' for supporting CSV exports
    exportSelectedHomeChargeReimbursementRecords: build.mutation<
      ExportSelectedHomeChargeReimbursementRecordsApiResponse,
      ExportSelectedHomeChargeReimbursementRecordsApiArg
    >({
      query: (queryArg) => ({
        url: `/evcharge/2025-08-26/export-selected-reimbursements/${queryArg.driverId}`,
        method: 'POST',
        body: queryArg.reimbursementExportRequestDto,
        headers: {
          'cs-organization': queryArg['cs-organization'],
          'cs-fleet': queryArg['cs-fleet'],
        },
        params: {
          timeZoneId: queryArg.timeZoneId,
          distanceUnit: queryArg.distanceUnit,
          temperatureUnit: queryArg.temperatureUnit,
        },
        responseHandler: 'text',
      }),
      invalidatesTags: ['reimbursements-export-and-reports-controller'],
    }),
    exportSelectedOrAllHomeChargeReimbursementRecords: build.mutation<
      ExportSelectedOrAllHomeChargeReimbursementRecordsApiResponse,
      ExportSelectedOrAllHomeChargeReimbursementRecordsApiArg
    >({
      query: (queryArg) => ({
        url: `/evcharge/2025-08-26/export-specified-reimbursements/${queryArg.driverId}`,
        method: 'POST',
        body: queryArg.reimbursementExportRequestDto,
        headers: {
          'cs-organization': queryArg['cs-organization'],
          'cs-fleet': queryArg['cs-fleet'],
        },
        params: {
          timeZoneId: queryArg.timeZoneId,
          pluginTimeAfterUTC: queryArg.pluginTimeAfterUtc,
          pluginTimeBeforeUTC: queryArg.pluginTimeBeforeUtc,
          distanceUnit: queryArg.distanceUnit,
          temperatureUnit: queryArg.temperatureUnit,
        },
        responseHandler: 'text',
      }),
      invalidatesTags: ['reimbursements-export-and-reports-controller'],
    }),
    exportOrdersV20260505: build.mutation<
      ExportOrdersV20260505ApiResponse,
      ExportOrdersV20260505ApiArg
    >({
      query: (queryArg) => ({
        url: '/ordertracking/2026-05-05/orders/export',
        method: 'POST',
        body: queryArg.ordersExportV20260505Dto,
        headers: { 'cs-organization': queryArg['cs-organization'] },
        params: {
          includeAutoImportedOrders: queryArg.includeAutoImportedOrders,
        },
        responseHandler: 'text',
      }),
      invalidatesTags: ['order-controller'],
    }),
    exportReportCsv: build.mutation<
      ExportReportCsvApiResponse,
      ExportReportCsvApiArg
    >({
      query: (queryArg) => ({
        url: `/reports/2025-10-21/${queryArg.reportType}/export-csv`,
        method: 'POST',
        body: queryArg.reportExportRequestBodyDto,
        headers: {
          'cs-organization': queryArg['cs-organization'],
          'cs-fleet': queryArg['cs-fleet'],
        },
        responseHandler: 'text',
      }),
      invalidatesTags: ['reports-controller'],
    }),
    adminExportReportCsv: build.mutation<
      AdminExportReportCsvApiResponse,
      AdminExportReportCsvApiArg
    >({
      query: (queryArg) => ({
        url: `/reports/2026-02-24/${queryArg.reportType}/export-csv`,
        method: 'POST',
        body: queryArg.reportExportRequestBodyDto,
        headers: {
          'cs-organization': queryArg['cs-organization'],
        },
        responseHandler: async (response) => response.blob(),
      }),
      invalidatesTags: ['reports-controller'],
    }),
    postAudit20250603ExportSync: build.mutation<
      PostAudit20250603ExportSyncApiResponse,
      PostAudit20250603ExportSyncApiArg
    >({
      query: (queryArg) => ({
        url: '/audit/2025-06-03/export/sync',
        method: 'POST',
        body: queryArg.auditExportRequest,
        headers: {
          'cs-organization': queryArg['cs-organization'],
          ...(queryArg['cs-fleet'] && { 'cs-fleet': queryArg['cs-fleet'] }),
        },
        responseHandler: 'text',
      }),
      invalidatesTags: ['audit-export-controller'],
    }),
    /** OpenAPI body is multipart; generated client passes a plain object — send `FormData` instead. */
    uploadFeedbackImage: build.mutation<
      UploadFeedbackImageApiResponse,
      UploadFeedbackImageApiArg
    >({
      query: (queryArg) => {
        const formData = new FormData();
        const file = queryArg.body.file;
        const filename = file instanceof File ? file.name : 'feedback-image';
        formData.append('file', file, filename);
        return {
          url: '/feedback/2026-04-21/images',
          method: 'POST',
          body: formData,
          formData: true,
          headers: {
            'cs-organization': queryArg['cs-organization'],
            'cs-fleet': queryArg['cs-fleet'],
          },
        };
      },
      invalidatesTags: ['feedback-image-upload-controller'],
    }),
    /** Binary image body; default fetch would parse JSON and fail. */
    downloadFeedbackImage: build.query<
      DownloadFeedbackImageApiResponse,
      DownloadFeedbackImageApiArg
    >({
      query: (queryArg) => ({
        url: '/feedback/2026-04-21/images',
        headers: {
          ...(queryArg['cs-organization'] != null &&
          queryArg['cs-organization'] !== ''
            ? { 'cs-organization': queryArg['cs-organization'] }
            : {}),
          ...(queryArg['cs-fleet'] != null && queryArg['cs-fleet'] !== ''
            ? { 'cs-fleet': queryArg['cs-fleet'] }
            : {}),
        },
        params: { blobName: queryArg.blobName },
        responseHandler: async (response) => response.blob(),
      }),
      providesTags: ['feedback-image-upload-controller'],
    }),

    exportPeripherals: build.mutation<
      ExportPeripheralsApiResponse,
      Omit<ExportPeripheralsApiArg, 'cs-organization'> & {
        'cs-organization'?: string;
      }
    >({
      query: (queryArg) => ({
        url: '/peripherals/2026-06-30/export',
        method: 'POST',
        body: queryArg.exportPeripheralsRequestDto,
        headers: {
          ...(queryArg['cs-organization'] && {
            'cs-organization': queryArg['cs-organization'],
          }),
        },
        responseHandler: async (response) => response.blob(),
      }),
    }),

    // Override to suppress the generated `invalidatesTags: ['peripheral-controller']`.
    // The retry flow registers the batch with the activation-progress poller;
    // the table refetches only after batch polling completes, not immediately
    // when the mutation returns.
    retryActivatePeripherals: build.mutation<
      RetryActivatePeripheralsApiResponse,
      RetryActivatePeripheralsApiArg
    >({
      query: (queryArg) => ({
        url: '/peripherals/2026-06-30/retry-activate',
        method: 'POST',
        body: queryArg.activatePeripheralsRequestDto,
        headers: {
          'x-bd-organization': queryArg['x-bd-organization'],
          ...(queryArg['x-bd-fleet'] && {
            'x-bd-fleet': queryArg['x-bd-fleet'],
          }),
        },
      }),
      invalidatesTags: [],
    }),

    verifyFan: build.mutation<VerifyFanApiResponse, VerifyFanApiArg>({
      query: ({ orgId, fan }) => ({
        url: `/organizations/2026-06-30/${orgId}/verify-fan`,
        method: 'POST',
        body: { fan },
      }),
      // Invalidate organization RTK Query caches (if used elsewhere).
      // Note: the verification banner reads from organizationsSlice (axios thunk),
      // not RTK Query — the banner is refreshed by dispatching getOrgDetails directly
      // after a successful verification in VerifyAccountModal.
      invalidatesTags: ['organization-v-2-controller'],
    }),
  }),
  overrideExisting: true,
});

/**
 * Re-export hooks whose response types differ from the generated spec so
 * downstream code gets the override-truthful type instead of the spec type.
 *
 * `useExportPeripheralsMutation` is re-exported here so callers get the
 * overridden endpoint (with `responseHandler: blob`) rather than the
 * generated one that tries to parse the CSV response as JSON.
 */
export const {
  useGetVehicleImageUrlWithParamsQuery,
  useVerifyFanMutation,
  useExportPeripheralsMutation,
} = enhancedApi;

export { enhancedApi as clientApi };
export default {};
